import React from 'react';
import { motion } from 'framer-motion';
import { FiChevronRight } from 'react-icons/fi';

interface OperationalModuleProps {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  status?: 'active' | 'warning' | 'error' | 'idle';
  statusLabel?: string;
  onClick?: () => void;
  isOpen?: boolean;
  children?: React.ReactNode;
  testId?: string;
  badge?: string | number;
}

const OperationalModule: React.FC<OperationalModuleProps> = ({
  title,
  subtitle,
  icon: Icon,
  status = 'active',
  statusLabel,
  onClick,
  isOpen,
  children,
  testId,
  badge,
}) => {
  const statusColors = {
    active: 'bg-green-500/20 text-green-400 border-green-500/30',
    warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    error: 'bg-red-500/20 text-red-400 border-red-500/30',
    idle: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  };

  const statusDotColors = {
    active: 'bg-green-400',
    warning: 'bg-yellow-400',
    error: 'bg-red-400',
    idle: 'bg-slate-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-slate-800/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 overflow-hidden"
      data-testid={testId}
    >
      <button
        onClick={onClick}
        className="w-full p-5 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
      >
        <div className="flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-purple-500/30">
            <Icon className="text-cyan-400 text-xl" />
          </div>
          <div className="text-left">
            <div className="flex items-center space-x-2">
              <h3 className="text-base font-semibold text-white">{title}</h3>
              {badge !== undefined && (
                <span className="px-2 py-0.5 text-xs font-medium bg-purple-500/20 text-purple-300 rounded-full border border-purple-500/30">
                  {badge}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-400">{subtitle}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {statusLabel && (
            <div className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center space-x-1.5 ${statusColors[status]}`}>
              <div className={`w-1.5 h-1.5 rounded-full ${statusDotColors[status]}`} />
              <span>{statusLabel}</span>
            </div>
          )}
          <motion.div
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <FiChevronRight className="text-slate-400 text-lg" />
          </motion.div>
        </div>
      </button>

      {isOpen && children && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="border-t border-slate-700/50"
        >
          <div className="p-5">
            {children}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default OperationalModule;